import type { ApolloCache } from '@apollo/client';
import { useApolloClient, useMutation } from '@apollo/client';
import type { DeleteOversightAssessmentsMutation } from '@risksmart-app/web-graphql-client/generated/graphql';
import { DeleteOversightAssessmentsDocument } from '@risksmart-app/web-graphql-client/generated/graphql';

import { useIsFeatureFlagEnabled } from '@/hooks/useIsFeatureFlagEnabled';
import { evictField } from '@/utils/graphqlUtils';

import { useDeleteOversightAssessmentsTRPC } from './useDeleteOversightAssessmentsTRPC';

/**
 * Apollo reads that this mutation invalidates.
 *
 * Shared by both branches on purpose. The migrated tRPC path still has to
 * evict these because the corresponding READS are NOT migrated and are
 * cache-first, so a write via tRPC otherwise leaves the Apollo-backed list or
 * counter showing pre-write data. Keeping one list means the two paths cannot
 * drift — that drift is what produced the stale oversight count.
 *
 * This lives in the WRAPPER, not in the `*TRPC` hook: the wrapper is the
 * flag-aware seam that already owns the Apollo mutation, so the tRPC hook
 * stays free of Apollo.
 */
const evictApolloReads = (cache: ApolloCache<unknown>) => {
  evictField(cache, 'oversight_assessment');
  evictField(cache, 'oversight_assessment_aggregate');
};

export const useDeleteOversightAssessments = () => {
  const trpcEnabled = useIsFeatureFlagEnabled('trpc');
  const apolloClient = useApolloClient();

  // GraphQL mutation
  const [deleteOversightAssessmentsGraphQL, graphqlState] = useMutation(
    DeleteOversightAssessmentsDocument,
    {
      update: (cache) => {
        evictApolloReads(cache);
      },
    }
  );

  // tRPC mutation
  const trpcMutation = useDeleteOversightAssessmentsTRPC();

  const deleteOversightAssessments = async (
    ids: string[]
  ): Promise<DeleteOversightAssessmentsMutation> => {
    if (trpcEnabled) {
      const trpcResult = await trpcMutation.deleteOversightAssessments(ids);
      evictApolloReads(apolloClient.cache);

      return trpcResult;
    }

    const result = await deleteOversightAssessmentsGraphQL({
      variables: { Ids: ids },
    });
    if (!result.data) {
      throw new Error('Failed to delete oversight assessments');
    }

    return result.data;
  };

  if (trpcEnabled) {
    return {
      deleteOversightAssessments,
      loading: trpcMutation.loading,
      error: trpcMutation.error,
    };
  }

  return {
    deleteOversightAssessments,
    loading: graphqlState.loading,
    error: graphqlState.error,
  };
};
